// src/pages/widgets/WidgetVersionListPage.jsx
import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, Button, Paper, CircularProgress, Alert,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, // TableCell & TableRow only for TableHead
  IconButton, Tooltip, Stack, Breadcrumbs, Link as MuiLink,
} from '@mui/material';
import {
  Add as AddIcon,
  ArrowBack as BackIcon, NavigateNext as NavigateNextIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material'; // Icons specific to this page's top actions
import { useNavigate, useParams, Link as RouterLink } from 'react-router-dom';
import { toast } from 'react-toastify';
import { widgetService } from '../../api/widgetService';
import EmbedCodeModal from '../../components/widgets/EmbedCodeModal';
import WidgetVersionListItem from '../../components/widgets/WidgetVersionListItem'; // IMPORT THE NEW COMPONENT
import InfoBox from '../../components/common/InfoBox'; // Assuming you have this

const WidgetVersionListPage = () => {
  const navigate = useNavigate();
  const { templateId } = useParams();

  const [versions, setVersions] = useState([]);
  const [templateInfo, setTemplateInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activatingId, setActivatingId] = useState(null);

  const [embedModalProps, setEmbedModalProps] = useState({
    open: false, versionId: null, templateId: null, templateName: null,
  });

  const fetchTemplateAndVersions = useCallback(async () => {
    if (!templateId) {
      setError("Template ID is missing from URL.");
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);

    const templateRes = await widgetService.getTemplate(templateId);
    if (templateRes.isError) {
      setError(templateRes.message || 'Failed to load template details.');
      toast.error(templateRes.message || 'Failed to load template details.');
      setTemplateInfo(null);
      setVersions([]);
      setLoading(false);
      return;
    }
    setTemplateInfo(templateRes);

    const versionsRes = await widgetService.listVersionsByTemplate(templateId);
    if (versionsRes.isError) {
      setError(versionsRes.message || 'Failed to load widget versions.');
      setVersions([]);
      toast.error(versionsRes.message || 'Failed to load widget versions.');
    } else {
      setVersions(Array.isArray(versionsRes) ? versionsRes.sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at)) : []);
    }
    setLoading(false);
  }, [templateId]);

  useEffect(() => {
    fetchTemplateAndVersions();
  }, [fetchTemplateAndVersions]);

  const handleActivate = async (versionIdToActivate) => {
    setActivatingId(versionIdToActivate);
    const res = await widgetService.activateVersion(versionIdToActivate);
    if (!res.isError) {
      toast.success(res.message || 'Version activated successfully.');
      fetchTemplateAndVersions();
    } else {
      toast.error(res.message || 'Failed to activate version.');
    }
    setActivatingId(null);
  };

  const handleDeleteVersion = async (versionIdToDelete) => {
    const versionToDelete = versions.find(v => v.id === versionIdToDelete);
    if (!window.confirm(`Are you sure you want to delete version "${versionToDelete?.version_name || versionToDelete?.name || 'this version'}"? This action cannot be undone.`)) return;

    setLoading(true);
    const res = await widgetService.deleteVersion(versionIdToDelete);
    setLoading(false);

    if (res.isError) {
      toast.error(res.message || 'Failed to delete version.');
    } else {
      toast.success('Widget version deleted successfully.');
      setVersions(prev => prev.filter(v => v.id !== versionIdToDelete));
    }
  };

  const handleOpenEmbedModal = (versionObject) => {
    setEmbedModalProps({
      open: true,
      versionId: versionObject.id,
      templateId: templateInfo?.id,
      templateName: templateInfo?.name,
    });
  };

  const handleCloseEmbedModal = () => {
    setEmbedModalProps({ open: false, versionId: null, templateId: null, templateName: null });
  };

  if (loading && !templateInfo) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" sx={{ p: 5, height: 'calc(100vh - 200px)' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} aria-label="breadcrumb" sx={{ mb: 2 }}>
        <MuiLink component={RouterLink} underline="hover" color="inherit" to="/widgets">
          Widget Templates
        </MuiLink>
        {templateInfo ? (
          <MuiLink component={RouterLink} underline="hover" color="inherit" to={`/widgets/${templateInfo.id}/edit`}>
            {templateInfo.name || `Template ...${templateId?.slice(-4)}`}
          </MuiLink>
        ) : (
          <Typography color="text.secondary">Loading Template...</Typography>
        )}
        <Typography color="text.primary">Versions</Typography>
      </Breadcrumbs>

      <Stack direction={{xs: 'column', sm: 'row'}} justifyContent="space-between" alignItems="center" mb={3} spacing={{xs:2, sm:1}}>
        <Typography variant="h4">
          Versions for: {templateInfo?.name || "Template"}
        </Typography>
        <Stack direction="row" spacing={1}>
          <Tooltip title="Refresh versions">
            <span>
              <IconButton onClick={fetchTemplateAndVersions} disabled={loading}>
                {loading && versions.length > 0 ? <CircularProgress size={24} /> : <RefreshIcon />}
              </IconButton>
            </span>
          </Tooltip>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate(`/widgets/${templateId}/versions/new`)}
            disabled={!templateId || loading}
          >
            Create New Version
          </Button>
        </Stack>
      </Stack>

      {error && !loading && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading && versions.length === 0 && !error && <Box display="flex" justifyContent="center" p={3}><CircularProgress /></Box>}

      {!loading && versions.length === 0 && !error && (
        <InfoBox type="info" title="No Widget Versions Found">
          This template does not have any versions yet. Click "Create New Version" to add one.
        </InfoBox>
      )}

      {versions.length > 0 && (
        <TableContainer component={Paper}>
          <Table sx={{ minWidth: 750 }} aria-label="widget versions table">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold', pl: 2, width: '30%' }}>Version Details</TableCell>
                <TableCell sx={{ fontWeight: 'bold', width: '25%' }}>Description</TableCell>
                <TableCell sx={{ fontWeight: 'bold', width: '15%' }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 'bold', width: '15%' }}>Last Updated</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold', pr: 2, width: '15%' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {versions.map(v => (
                <WidgetVersionListItem
                  key={v.id}
                  version={v}
                  onActivate={handleActivate}
                  onDelete={handleDeleteVersion}
                  onOpenEmbedModal={handleOpenEmbedModal}
                  isActivating={activatingId === v.id}
                  isLoadingActions={loading && versions.length > 0}
                  showDescription={true}
                  showUpdatedAt={true}
                />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <EmbedCodeModal
        open={embedModalProps.open}
        onClose={handleCloseEmbedModal}
        versionId={embedModalProps.versionId}
        templateId={embedModalProps.templateId}
        templateName={embedModalProps.templateName}
      />
      <Button
        variant="outlined"
        startIcon={<BackIcon />}
        onClick={() => navigate('/widgets')}
        sx={{ mt: 3 }}
      >
        Back to All Templates
      </Button>
    </Box>
  );
};

export default WidgetVersionListPage;